/**
 * Created: 2026-10-15
 * Initial commit: Admin schedules coaching appointments from the slots page
 *
 * Coaching appointment rules shared by the mini coach appoint flow and the
 * web admin slots page, so both offer what `createCoachingAppointment` accepts.
 *
 * Updated: 2026-10-19 - A selection may mix courts of different sports: a
 * tennis course for a child or beginner can be taught on a pickleball court
 */

/** One person may bring at most one companion (e.g. a parent with a child). */
export const MAX_HEADCOUNT = 2

/** One court-hour of an appointment selection. */
export interface CoachSelectedSlot {
  courtId: string
  hour: number
}

/**
 * Whether `slots` may be scheduled as one coaching appointment, mirroring
 * `createCoachingAppointment`'s checks: at least one slot, one court per hour,
 * and the hours consecutive. Courts may differ
 * from hour to hour. The date is not checked: the picker shows one date.
 */
export function isValidCoachSelection(slots: CoachSelectedSlot[]) {
  if (slots.length === 0) return false
  const hours = slots.map((s) => s.hour)
  if (new Set(hours).size !== hours.length) return false
  return Math.max(...hours) - Math.min(...hours) + 1 === hours.length
}

/** Heads a student's available hours pay for: each head draws `sessionHours`. */
export function hoursCapOf(availableHours: number, sessionHours: number) {
  return Math.floor(availableHours / Math.max(sessionHours, 1))
}

/**
 * Most people one invited student may bring, themselves included: capped by
 * `MAX_HEADCOUNT`, by the seats left once the others' headcounts are taken,
 * and by the student's hours. Never below 1.
 */
export function maxHeadcountOf({
  seats,
  totalHeadcount,
  ownHeadcount,
  availableHours,
  sessionHours,
}: {
  seats: number
  totalHeadcount: number
  ownHeadcount: number
  availableHours: number
  sessionHours: number
}) {
  return Math.max(
    1,
    Math.min(
      MAX_HEADCOUNT,
      seats - (totalHeadcount - ownHeadcount),
      hoursCapOf(availableHours, sessionHours),
    ),
  )
}
